import React, { useState } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SESSION_STATUSES } from '../utils/constants';
import './RatingModal.css';

function RatingModal({ request, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }

    setSubmitting(true);
    if (process.env.NODE_ENV === 'development') {
      console.log('RatingModal: submitting rating for session_request_id:', request.id, 'rating:', rating);
    }
    try {
      const { error: insertErr } = await supabase.from('ratings').insert({
        session_request_id: request.id,
        parent_id: user.id,
        mentor_id: request.mentor_id,
        rating,
        comment: comment.trim() || null,
      });
      if (insertErr) throw insertErr;

      // Mark session as completed if not already
      if (request.status !== SESSION_STATUSES.COMPLETED) {
        await supabase
          .from('session_requests')
          .update({ status: SESSION_STATUSES.COMPLETED })
          .eq('id', request.id)
          .eq('parent_id', user.id);
      }

      onSubmitted();
    } catch (err) {
      setError(err.message || 'Failed to submit rating. Please try again.');
      if (process.env.NODE_ENV === 'development') {
        console.error('RatingModal submit error:', err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const mentorName = request.mentor
    ? `${request.mentor.first_name} ${request.mentor.last_name}`.trim()
    : 'your trainer';

  return (
    <div className="rating-modal-overlay" role="dialog" aria-modal="true" aria-label="Leave a review">
      <div className="rating-modal-box card">
        <button className="rating-modal-close" onClick={onClose} aria-label="Close">×</button>

        <h2 className="rating-modal-title">Rate Your Session</h2>
        <p className="rating-modal-sub">How was your session with <strong>{mentorName}</strong>?</p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {/* Star picker */}
          <div className="rating-stars-picker" role="group" aria-label="Star rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`rating-star-btn${star <= (hovered || rating) ? ' active' : ''}`}
                onMouseEnter={() => setHovered(star)}
                onMouseLeave={() => setHovered(0)}
                onClick={() => setRating(star)}
                aria-label={`${star} star${star !== 1 ? 's' : ''}`}
              >
                ★
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="rating-label">{['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent!'][rating]}</p>
          )}

          <div className="form-group" style={{ marginTop: '1.25rem' }}>
            <label className="form-label" htmlFor="rating-comment">
              Written Review (Optional)
            </label>
            <textarea
              id="rating-comment"
              className="form-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience…"
              rows={3}
            />
          </div>

          <div className="rating-modal-actions">
            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={submitting || rating === 0}
            >
              {submitting ? 'Submitting…' : 'Submit Review'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-full"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RatingModal;
